import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
    AuthenticatedUserDto,
    ErrorResponse,
    OtpRequestDto,
    OtpResponseDto,
    ResetPasswordRequestDto,
    ResetPasswordResponseDto
} from '../../@api';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationManager, AuthenticationNotificationService, ProgressIndicatorService} from '../../@shared';
import {DOCUMENT} from '@angular/common';
import {environment} from '../../../environments/environment';

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent implements OnInit {

    @Input() heading: string;
    @Input() headingLabel: string;
    @Input() returnUrl: string;

    signInLabel: string;
    loginForm: FormGroup;
    isSignIn: boolean;
    isRequestOtp: boolean;
    isResetPassword: boolean;

    requestOtpForm: FormGroup;

    passwordResetForm: FormGroup;
    otp: OtpResponseDto;

    constructor(private router: Router,
                private fb: FormBuilder,
                private route: ActivatedRoute,
                public authenticationManager: AuthenticationManager,
                private authenticationNotificationManager: AuthenticationNotificationService,
                private progressIndicatorService: ProgressIndicatorService,
                @Inject(DOCUMENT) public document: Document) {
        this.signInLabel = 'Welcome, please use the form to sign-in';
        this.isSignIn = true;
        this.isRequestOtp = this.isResetPassword = false;

        this.authenticationNotificationManager.event$.subscribe(item => this.onAuthenticationNotificationEvent(item))
    }

    ngOnInit(): void {
        this.initialiseForm();
    }

    login(): void {
        this.loginForm.controls['username'].markAsDirty();
        this.loginForm.controls['password'].markAsDirty();

        if (this.loginForm.invalid) {
            return;
        }

        this.progressIndicatorService.publish(true);
        setTimeout((): void => {
            this.authenticationManager.authenticate(this.loginForm.value).subscribe((result: boolean): void => {
                this.progressIndicatorService.publish(false);
                if (result === true) {
                    if (this.returnUrl && !this.returnUrl.toLowerCase().startsWith('http')) {
                        this.router.navigate([this.returnUrl]);
                    } else {
                        this.router.navigate(['home']);
                    }
                } else {
                    this.loginForm.controls['password'].setErrors({'failed': true});
                }
            }, error => {
                console.log(error);
                this.progressIndicatorService.publish(false);
                this.loginForm.controls['password'].setErrors({'failed': true});
            });
        }, 1000);
    }

    logout(): void {
        this.authenticationManager.logout();
    }

    forgotPassword(): void {
        this.progressIndicatorService.publish(true);
        setTimeout((): void => {
            this.isSignIn = this.isResetPassword = false;
            this.isRequestOtp = true;
            this.progressIndicatorService.publish(false);

            this.requestOtpForm = this.fb.group({
                username: [this.loginForm.controls['username']?.value, [Validators.required]]
            });
        }, 1000);
    }

    requestOtp(): void {
        this.requestOtpForm.controls['username'].markAsDirty();

        if (this.requestOtpForm.invalid) {
            return;
        }

        let request: OtpRequestDto = {
            profile: environment.access_profile,
            username: this.requestOtpForm.controls['username'].value
        };

        this.progressIndicatorService.publish(true);
        this.authenticationManager.requestOtp(request).subscribe((response: OtpResponseDto): void => {
            setTimeout((): void => {
                this.otp = response;
                let error: ErrorResponse = response.error;

                if (error && error.message) {
                    this.requestOtpForm.controls['username'].setErrors({'not-found': true});
                } else {
                    this.isRequestOtp = false;
                    this.isResetPassword = true;
                }
                this.progressIndicatorService.publish(false);
            }, 1000);
        }, error => {
            console.log(error);
            this.progressIndicatorService.publish(false);
            this.requestOtpForm.controls['username'].setErrors({'error': true});
        });
    }

    backToSignIn(): void {
        this.progressIndicatorService.publish(true);
        setTimeout((): void => {
            this.isSignIn = true;
            this.isRequestOtp = this.isResetPassword = false;
            this.progressIndicatorService.publish(false);

            this.loginForm = this.fb.group({
                username: [this.requestOtpForm.controls['username']?.value, [Validators.required]],
                password: [undefined, Validators.required]
            });
        }, 1000);
    }

    resetPassword(): void {
        this.passwordResetForm.controls['password'].markAsDirty();
        this.passwordResetForm.controls['retypePassword'].markAsDirty();
        this.passwordResetForm.controls['otp'].markAsDirty();

        if (this.passwordResetForm.invalid) {
            return;
        }

        let password = this.passwordResetForm.controls['password'].value;
        let retypePassword = this.passwordResetForm.controls['retypePassword'].value;

        if (password !== retypePassword) {
            this.passwordResetForm.controls['retypePassword'].setErrors({'mismatch': true});
        } else {
            let otp: string = this.passwordResetForm.controls['otp'].value;
            if (otp !== this.otp.otp) {
                this.passwordResetForm.controls['otp'].setErrors({'mismatch': true});
            } else {
                /** All is good, password can be reset */
                let request: ResetPasswordRequestDto = {
                    username: this.requestOtpForm.controls['username'].value,
                    newPassword: password
                };
                this.progressIndicatorService.publish(true);
                this.authenticationManager.resetPassword(request).subscribe((response: ResetPasswordResponseDto): void => {
                    setTimeout((): void => {
                        let error: ErrorResponse = response.error;
                        if (error && error.message) {
                            this.passwordResetForm.controls['password'].setErrors({'error': true});
                        } else {
                            this.isRequestOtp = this.isResetPassword = false;
                            this.isSignIn = true;
                            this.signInLabel = 'Your password was changed successfully!';
                            this.loginForm = this.fb.group({
                                username: [this.requestOtpForm.controls['username']?.value, [Validators.required]],
                                password: [undefined, Validators.required]
                            });
                        }
                        this.progressIndicatorService.publish(false);
                    }, 1000);
                }, error => {
                    console.log(error);
                    this.progressIndicatorService.publish(false);
                    this.passwordResetForm.controls['password'].setErrors({'error': true});
                });
            }
        }
    }

    initialiseForm(): void {
        let authenticatedUser: AuthenticatedUserDto = this.authenticationManager.authenticatedUser;

        this.loginForm = this.fb.group({
            username: [(authenticatedUser) ? {value: authenticatedUser.userDto.email, disabled: true} : undefined, [Validators.required]],
            password: [(authenticatedUser) ? {value: authenticatedUser.userDto.passwd, disabled: true} : undefined, Validators.required]
        });

        this.requestOtpForm = this.fb.group({
            username: [undefined, [Validators.required]]
        });

        this.passwordResetForm = this.fb.group({
            password: [undefined, [Validators.required]],
            retypePassword: [undefined, [Validators.required]],
            otp: [undefined, Validators.required]
        });
    }

    onAuthenticationNotificationEvent(authenticated: boolean): void {
        if (authenticated) {
            this.loginForm.controls['username'].disable();
            this.loginForm.controls['password'].disable();
        } else {
            this.loginForm.controls['username'].enable();
            this.loginForm.controls['password'].enable();
            this.loginForm.controls['password'].setValue(undefined);
            this.loginForm.controls['password'].setErrors(null);
        }
    }
}
